// POC "neutral foundation" skin smoothing pass.
//
// Applies a mild Gaussian blur to the skin region only (face oval minus
// eyes and mouth), composited back at low opacity so the original texture
// — pores, moles, freckles, scars — survives. This is NOT a foundation
// shade simulator; it doesn't shift hue or luminance, only evens the
// tonal variance so blush/eyeshadow/lipstick layers sit on a calmer base.
//
// Scope per user 2026-04-20: "minimo impatto per non snaturare le
// imperfezioni". Default blur is ~0.3% of the shorter edge (~5px on a
// 1500px photo) and the smoothed layer is alpha-blended at 30%.

import type { LipDetection } from '../tracking/faceLandmarker';
import type { SemanticFaceMap } from '../semantic/semanticFaceMap';
import { stackBlur } from './stackBlur';

export interface SkinRenderResult {
  canvas: HTMLCanvasElement;
}

export interface SkinSmoothingOptions {
  blurPx?: number;
  opacity?: number;
  semanticMap?: SemanticFaceMap | null;
}

// MediaPipe FaceMesh face-oval landmark loop (clockwise from forehead).
const FACE_OVAL = [
  10, 338, 297, 332, 284, 251, 389, 356, 454, 323,
  361, 288, 397, 365, 379, 378, 400, 377, 152, 148,
  176, 149, 150, 136, 172, 58, 132, 93, 234, 127,
  162, 21, 54, 103, 67, 109
];

// Closed eye contours — upper + lower lid, one full ring per eye.
const LEFT_EYE = [
  33, 246, 161, 160, 159, 158, 157, 173, 133,
  155, 154, 153, 145, 144, 163, 7
];
const RIGHT_EYE = [
  263, 466, 388, 387, 386, 385, 384, 398, 362,
  382, 381, 380, 374, 373, 390, 249
];

// Outer lip contour — must match the renderer's outer lip ring so the
// skin blur stops exactly where the lipstick render starts.
const OUTER_LIP = [
  61, 185, 40, 39, 37, 0, 267, 269, 270, 409, 291,
  375, 321, 405, 314, 17, 84, 181, 91, 146
];

function tracePath(
  ctx: CanvasRenderingContext2D,
  detection: LipDetection,
  indices: readonly number[]
): void {
  const { landmarks, imageWidth: W, imageHeight: H } = detection;
  let started = false;
  for (const idx of indices) {
    const p = landmarks[idx];
    if (!p) continue;
    const x = p.x * W;
    const y = p.y * H;
    if (!started) {
      ctx.moveTo(x, y);
      started = true;
    } else {
      ctx.lineTo(x, y);
    }
  }
  if (started) ctx.closePath();
}

export function renderSkinSmoothing(
  source: HTMLCanvasElement | ImageBitmap,
  detection: LipDetection,
  options: SkinSmoothingOptions = {}
): SkinRenderResult {
  const W = detection.imageWidth;
  const H = detection.imageHeight;

  // Defaults: ~0.3% short-edge blur, 30% opacity. Tuned to sit below the
  // "noticeable filter" threshold on front-camera phone shots.
  const blurPx  = options.blurPx  ?? Math.max(2, Math.min(W, H) * 0.003);
  const opacity = options.opacity ?? 0.30;

  // 1. Output canvas = original photo.
  const out = document.createElement('canvas');
  out.width = W;
  out.height = H;
  const octx = out.getContext('2d');
  if (!octx) throw new Error('2D context unavailable');
  octx.drawImage(source, 0, 0, W, H);

  // 2. Blurred copy of the full photo.
  const blurred = document.createElement('canvas');
  blurred.width = W;
  blurred.height = H;
  const bctx = blurred.getContext('2d', { willReadFrequently: true });
  if (!bctx) return { canvas: out };
  bctx.drawImage(source, 0, 0, W, H);
  stackBlur(bctx, 0, 0, W, H, blurPx);

  // 3. Skin mask = face oval MINUS eyes MINUS outer-lip. Drawn with
  // evenodd so inner subpaths punch holes. Mild edge feather so the
  // smoothed region doesn't have a visible seam at the silhouette.
  const mask = document.createElement('canvas');
  mask.width = W;
  mask.height = H;
  const mctx = mask.getContext('2d', { willReadFrequently: true });
  if (!mctx) return { canvas: out };
  const featherPx = Math.max(3, Math.min(W, H) * 0.008);
  if (options.semanticMap && options.semanticMap.width === W && options.semanticMap.height === H) {
    mctx.drawImage(options.semanticMap.masks.skin, 0, 0);
  } else {
    mctx.fillStyle = '#ffffff';
    mctx.beginPath();
    tracePath(mctx, detection, FACE_OVAL);
    tracePath(mctx, detection, LEFT_EYE);
    tracePath(mctx, detection, RIGHT_EYE);
    tracePath(mctx, detection, OUTER_LIP);
    mctx.fill('evenodd');
  }
  stackBlur(mctx, 0, 0, W, H, featherPx);

  // 4. Keep only the skin-region of the blurred copy.
  bctx.globalCompositeOperation = 'destination-in';
  bctx.drawImage(mask, 0, 0);
  bctx.globalCompositeOperation = 'source-over';

  // 5. Composite that onto the original at low opacity.
  octx.globalAlpha = opacity;
  octx.drawImage(blurred, 0, 0);
  octx.globalAlpha = 1;

  return { canvas: out };
}
